import type { Document } from "../document/Document";
import type { GroupLayer, Layer, RasterLayer } from "../document/Layer";
import { compositeLayer } from "../pixel/blend";
import type { Command } from "./Command";

// Composite a raster layer onto the raster layer below it in the same parent.
//
// Caveats / simplifications (documented):
// - Raster-only. If either the layer or the one below isn't a raster, the
//   command is a no-op (with a console.warn).
// - The merged result is placed at the lower layer's position and uses the
//   lower layer's canvas-size pixel buffer. The upper layer's offset, blend
//   mode and opacity are honored via `compositeLayer`.
// - The upper layer's mask, adjustment effects etc. are not modeled in the
//   document yet, so they don't apply.
// - Invert restores the pre-merge state of the lower layer's pixel buffer
//   and re-inserts the deleted upper layer at its original index.

interface Snapshot {
  lowerLayerId: string;
  lowerBefore: Uint8ClampedArray;
  upper: RasterLayer;
  upperIndex: number;
  parentId: string | null;
}

export class MergeDownCommand implements Command {
  readonly kind = "merge-down";
  private snapshot: Snapshot | null = null;

  constructor(private readonly layerId: string) {}

  apply(doc: Document): Document {
    const located = locate(doc.layers, this.layerId, null);
    if (!located) return doc;
    const { siblings, index, parentId } = located;
    if (index === 0) {
      // Nothing below to merge with.
      return doc;
    }
    const upper = siblings[index]!;
    const lower = siblings[index - 1]!;
    if (upper.kind !== "raster" || lower.kind !== "raster") {
      console.warn(
        `MergeDownCommand: skipped — both layers must be raster (got ${upper.kind} / ${lower.kind})`,
      );
      return doc;
    }

    // Snapshot before mutating the lower layer's buffer.
    this.snapshot = {
      lowerLayerId: lower.id,
      lowerBefore: new Uint8ClampedArray(lower.pixels.data),
      upper,
      upperIndex: index,
      parentId,
    };

    // Composite upper -> lower. Both layers' pixel buffers may differ in size
    // and offset; project the upper onto a buffer matching the lower's
    // geometry, then `compositeLayer` does the blend in-place.
    const projected = projectInto(lower, upper);
    compositeLayer(lower.pixels.data, projected, upper.blendMode, upper.opacity);
    lower.pixels.markDirty();

    // Remove the upper layer from the tree.
    return { ...doc, layers: removeById(doc.layers, upper.id) };
  }

  invert(before: Document, _after: Document): Command {
    const snap = this.snapshot;
    return new RestoreMergeDownCommand(snap, before, this.layerId);
  }
}

// Internal inverse: restores the lower layer's buffer and re-inserts the
// upper layer at its original index/parent.
class RestoreMergeDownCommand implements Command {
  readonly kind = "merge-down/restore";
  constructor(
    private readonly snapshot: Snapshot | null,
    private readonly fallbackBefore: Document,
    private readonly upperLayerId: string,
  ) {}

  apply(doc: Document): Document {
    const snap = this.snapshot;
    if (!snap) return this.fallbackBefore;

    // Restore the lower layer's pixel data in-place.
    const lower = findRaster(doc.layers, snap.lowerLayerId);
    if (lower) {
      lower.pixels.data.set(snap.lowerBefore);
      lower.pixels.markDirty();
    }

    // Re-insert the upper layer at its original index in the same parent.
    return { ...doc, layers: insertAt(doc.layers, snap.upper, snap.parentId, snap.upperIndex) };
  }

  invert(_before: Document, _after: Document): Command {
    return new MergeDownCommand(this.upperLayerId);
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface Located {
  siblings: readonly Layer[];
  index: number;
  parentId: string | null;
}

function locate(layers: readonly Layer[], id: string, parentId: string | null): Located | null {
  for (let i = 0; i < layers.length; i++) {
    const l = layers[i]!;
    if (l.id === id) return { siblings: layers, index: i, parentId };
    if (l.kind === "group") {
      const inner = locate(l.children, id, l.id);
      if (inner) return inner;
    }
  }
  return null;
}

// Build a buffer in the lower layer's coordinate system that contains the
// upper layer's pixels, accounting for their different (x,y) offsets and
// canvas sizes. Pixels outside the upper's footprint are zero (transparent).
function projectInto(lower: RasterLayer, upper: RasterLayer): Uint8ClampedArray {
  const lw = lower.pixels.width;
  const lh = lower.pixels.height;
  const uw = upper.pixels.width;
  const uh = upper.pixels.height;
  const out = new Uint8ClampedArray(lw * lh * 4);
  // Offset of the upper relative to the lower.
  const dx = upper.x - lower.x;
  const dy = upper.y - lower.y;
  const x0 = Math.max(0, dx);
  const y0 = Math.max(0, dy);
  const x1 = Math.min(lw, dx + uw);
  const y1 = Math.min(lh, dy + uh);
  for (let y = y0; y < y1; y++) {
    const srcRow = y - dy;
    for (let x = x0; x < x1; x++) {
      const srcCol = x - dx;
      const srcOff = (srcRow * uw + srcCol) * 4;
      const dstOff = (y * lw + x) * 4;
      out[dstOff] = upper.pixels.data[srcOff]!;
      out[dstOff + 1] = upper.pixels.data[srcOff + 1]!;
      out[dstOff + 2] = upper.pixels.data[srcOff + 2]!;
      out[dstOff + 3] = upper.pixels.data[srcOff + 3]!;
    }
  }
  return out;
}

function findRaster(layers: readonly Layer[], id: string): RasterLayer | null {
  for (const l of layers) {
    if (l.kind === "raster" && l.id === id) return l;
    if (l.kind === "group") {
      const inner = findRaster(l.children, id);
      if (inner) return inner;
    }
  }
  return null;
}

function removeById(layers: readonly Layer[], id: string): Layer[] {
  const out: Layer[] = [];
  for (const l of layers) {
    if (l.id === id) continue;
    if (l.kind === "group") {
      const group: GroupLayer = { ...l, children: removeById(l.children, id) };
      out.push(group);
    } else {
      out.push(l);
    }
  }
  return out;
}

function insertAt(
  layers: readonly Layer[],
  layer: Layer,
  parentId: string | null,
  index: number,
): Layer[] {
  if (parentId === null) {
    const clamped = Math.max(0, Math.min(index, layers.length));
    return [...layers.slice(0, clamped), layer, ...layers.slice(clamped)];
  }
  return layers.map((l) => {
    if (l.id === parentId && l.kind === "group") {
      const clamped = Math.max(0, Math.min(index, l.children.length));
      const group: GroupLayer = {
        ...l,
        children: [...l.children.slice(0, clamped), layer, ...l.children.slice(clamped)],
      };
      return group;
    }
    if (l.kind === "group") {
      return { ...l, children: insertAt(l.children, layer, parentId, index) };
    }
    return l;
  });
}
